import { router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { highlight } from '../../lib/highlight'
import { useI18n } from '../../lib/useI18n'
import Icon, { type IconName } from './Icon'

// ⌘K command palette (mockups `.cmdk`) — a centered combobox that opens from the
// top-bar search and from ⌘K / Ctrl-K. It filters the surface's nav commands as
// you type and, with a query, queries the backend search for records (debounced).
// Arrow keys move the selection; Enter opens it; Esc closes.

export type Command = { label: string; icon: IconName; href: string }
type Result = {
  type: string
  id: string
  label: string
  subtitle?: string | null
  po?: string | null
  path: string
}

// Icon per searchable record type (falls back to a generic glyph).
const RESULT_ICON: Record<string, IconName> = {
  Shipment: 'package',
  Quote: 'file-text',
  Invoice: 'receipt',
  Customer: 'building-2',
  Carrier: 'truck',
}
type Row =
  | { kind: 'command'; label: string; icon: IconName; href: string }
  | {
      kind: 'result'
      label: string
      subtitle?: string | null
      po?: string | null
      icon: IconName
      href: string
      badge: string
    }
  | { kind: 'all'; label: string; icon: IconName; href: string }
  | { kind: 'recent'; label: string; icon: IconName; term: string }

const RECENT_LIMIT = 6

// Recent searches live in localStorage, scoped per user+surface by `storageKey`
// (the two surfaces are separate origins, so they never share storage). It's UI
// convenience only — the typed terms, not results, which stay access-controlled.
function loadRecent(key: string): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) ?? '[]')
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

export default function CommandPalette({
  open,
  onClose,
  commands,
  searchPath,
  storageKey = 'g4:recent-search',
}: {
  open: boolean
  onClose: () => void
  commands: Command[]
  searchPath: string
  storageKey?: string
}) {
  const { t } = useI18n()
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Result[]>([])
  const [active, setActive] = useState(0)
  const [recent, setRecent] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset + focus on open; refresh the recent list from storage.
  useEffect(() => {
    if (open) {
      setQ('')
      setResults([])
      setActive(0)
      setRecent(loadRecent(storageKey))
      // focus after the portal paints
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open, storageKey])

  function remember(value: string) {
    const v = value.trim()
    if (!v) return
    const next = [v, ...recent.filter((r) => r.toLowerCase() !== v.toLowerCase())].slice(
      0,
      RECENT_LIMIT,
    )
    setRecent(next)
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next))
    } catch {
      // storage unavailable (private mode / quota) — recents are best-effort.
    }
  }

  function clearRecent() {
    setRecent([])
    try {
      window.localStorage.removeItem(storageKey)
    } catch {
      // ignore
    }
  }

  // Debounced backend search for records.
  useEffect(() => {
    if (!open) return
    const term = q.trim()
    if (!term) {
      setResults([])
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetch(`${searchPath}.json?q=${encodeURIComponent(term)}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((data: { results: Result[] }) => setResults(data.results ?? []))
        .catch(() => {})
    }, 180)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [q, open, searchPath])

  const term = q.trim()
  const matchedCommands = term
    ? commands.filter((c) => c.label.toLowerCase().includes(term.toLowerCase()))
    : commands

  // With no query, surface recent searches above the nav commands; clicking one
  // re-runs it. With a query, it's filtered commands + record results + an "all".
  const showRecent = !term && recent.length > 0
  const rows: Row[] = [
    ...(showRecent
      ? recent.map((r): Row => ({ kind: 'recent', label: r, icon: 'clock', term: r }))
      : []),
    ...matchedCommands.map(
      (c): Row => ({ kind: 'command', label: c.label, icon: c.icon, href: c.href }),
    ),
    ...results.map(
      (r): Row => ({
        kind: 'result',
        label: r.label,
        subtitle: r.subtitle,
        po: r.po,
        icon: RESULT_ICON[r.type] ?? 'search',
        href: r.path,
        badge: r.type,
      }),
    ),
  ]
  if (term)
    rows.push({
      kind: 'all',
      label: t('cmdk.search_all', { term }),
      icon: 'search',
      href: `${searchPath}?q=${encodeURIComponent(term)}`,
    })

  // Keep the active index in range as the row set changes.
  const clampedActive = rows.length ? Math.min(active, rows.length - 1) : 0

  function activate(row: Row | undefined) {
    if (!row) return
    // A recent search just refills the box and re-runs — it doesn't navigate.
    if (row.kind === 'recent') {
      setQ(row.term)
      setActive(0)
      requestAnimationFrame(() => inputRef.current?.focus())
      return
    }
    // Record the typed query when it actually led somewhere searchy.
    if (term && (row.kind === 'result' || row.kind === 'all')) remember(term)
    onClose()
    router.visit(row.href)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (rows.length ? (i + 1) % rows.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      activate(rows[clampedActive])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  if (!open) return null

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-catcher
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center bg-[rgba(7,16,29,0.45)] px-4 pt-[14vh] backdrop-blur-[3px]"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-label="Command palette"
        className="w-[560px] max-w-[90vw] overflow-hidden rounded-2xl border border-border bg-surface shadow-[var(--shadow)]"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 border-border border-b px-[18px] py-[15px]">
          <Icon name="search" size={18} className="flex-none text-ink-muted" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            placeholder={t('cmdk.placeholder')}
            aria-label="Search or run a command"
            className="w-full border-0 bg-transparent text-[15px] text-ink outline-none focus:border-0 focus:ring-0 placeholder:text-ink-muted"
          />
          <kbd className="flex-none rounded-md border border-border-2 px-1.5 font-mono text-[10.5px] text-ink-muted">
            esc
          </kbd>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-1.5">
          {showRecent ? (
            <div className="flex items-center justify-between px-4 pt-1 pb-1">
              <span className="font-bold text-[10px] text-ink-muted uppercase tracking-[0.08em]">
                {t('cmdk.recent')}
              </span>
              <button
                type="button"
                onClick={clearRecent}
                className="font-semibold text-[11px] text-ink-2 hover:text-accent"
              >
                {t('cmdk.clear_recent')}
              </button>
            </div>
          ) : null}
          {rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('cmdk.no_matches')}</p>
          ) : (
            rows.map((row, i) => (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and rebuilt per keystroke
                key={`${row.kind}-${i}`}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => activate(row)}
                className={`flex w-full items-center gap-3 px-4 py-[11px] text-left text-[13.5px] ${
                  i === clampedActive ? 'bg-surface-3' : 'hover:bg-surface-3'
                }`}
              >
                <Icon name={row.icon} size={16} className="flex-none text-ink-2" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {row.kind === 'result' ? highlight(row.label, term) : row.label}
                  </span>
                  {row.kind === 'result' && row.subtitle ? (
                    <span className="block truncate text-[12px] text-ink-muted">
                      {highlight(row.subtitle, term)}
                    </span>
                  ) : null}
                  {row.kind === 'result' && row.po ? (
                    <span className="block truncate font-mono text-[11px] text-accent-strong">
                      {t('cmdk.po')} {highlight(row.po, term)}
                    </span>
                  ) : null}
                </span>
                {row.kind === 'result' ? (
                  <span className="flex-none rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-ink-muted">
                    {row.badge}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
